import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";

export const About = () => {
  return (
    <div data-testid="about-page" className="bg-white">
      <section className="border-b border-border">
        <div className="mx-auto max-w-[1280px] px-6 py-20 md:py-24 lg:px-10">
          <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-muted-foreground">
            About · KATLAS & Cierge Labs
          </p>
          <h1 className="mt-6 max-w-4xl font-serif text-5xl font-light leading-[1.05] tracking-tight sm:text-6xl">
            Operational governance, not platform centralisation.
          </h1>
          <p className="mt-8 max-w-3xl text-base leading-relaxed text-muted-foreground sm:text-lg">
            KATLAS is the operational governance layer for accountable
            coordination across people, systems, and AI. Cierge is the family
            of applied use cases that sit on KATLAS. Cierge Labs is the bounded
            design and sandbox layer where a visitor can explore how KATLAS
            would apply to their own context.
          </p>
        </div>
      </section>

      <section className="border-b border-border bg-[#FAFAFA]">
        <div className="mx-auto grid max-w-[1280px] gap-12 px-6 py-20 md:grid-cols-12 md:py-24 lg:px-10">
          <div className="md:col-span-5">
            <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-muted-foreground">
              Layering
            </p>
            <h2 className="mt-4 font-serif text-4xl font-light tracking-tight">
              Two layers, intentionally separated.
            </h2>
          </div>
          <div className="md:col-span-7">
            <ul className="grid gap-px bg-border">
              <li className="bg-white p-7">
                <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                  Cierge Labs · front
                </p>
                <h3 className="mt-3 font-serif text-2xl font-light tracking-tight">
                  Guided design and exploration
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                  White-label, low-cost, fast to deploy. Bounded selection
                  flow, tailored output, optional wallet, route to demo or
                  sandbox. Synthetic by default.
                </p>
              </li>
              <li className="bg-white p-7">
                <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                  KATLAS · trust-critical
                </p>
                <h3 className="mt-3 font-serif text-2xl font-light tracking-tight">
                  Custody, signed receipts, governed sharing
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                  Progressively owns the trust-critical surface: wallet
                  custody, selective disclosure, signed receipts, governed
                  sharing, resilient production deployment.
                </p>
              </li>
            </ul>
          </div>
        </div>
      </section>

      <section className="border-b border-border">
        <div className="mx-auto max-w-[1280px] px-6 py-20 md:py-24 lg:px-10">
          <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-muted-foreground">
            Studio scope
          </p>
          <h2 className="mt-4 max-w-3xl font-serif text-4xl font-light leading-tight tracking-tight">
            What this studio does — and what it explicitly does not.
          </h2>
          <div className="mt-12 grid gap-px bg-border md:grid-cols-2">
            <div className="bg-white p-8">
              <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-emerald-800">
                In scope
              </p>
              <ul className="mt-4 space-y-3 text-sm leading-relaxed text-foreground">
                <li>· Guided selection flow across role, sector, problem, scope</li>
                <li>· Tailored explainer and synthetic case study</li>
                <li>· Recommended next-step path (demo, sandbox, pilot)</li>
                <li>· Optional, browser-held wallet (ECDSA P-256)</li>
                <li>· Saved use-case drafts bound to wallet fingerprint</li>
                <li>· Mocked sandbox / pilot request capture</li>
                <li>· White-label deployment surface</li>
              </ul>
            </div>
            <div className="bg-white p-8">
              <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-destructive">
                Out of scope
              </p>
              <ul className="mt-4 space-y-3 text-sm leading-relaxed text-foreground">
                <li>· Open-ended copilot or freeform AI authoring</li>
                <li>· Production data ingestion or central document vault</li>
                <li>· Real cryptographic receipts (simulated in v1)</li>
                <li>· Cross-organisation key recovery</li>
                <li>· Deep enterprise SSO or RBAC</li>
                <li>· Live integrations into operator systems</li>
                <li>· Migration to KATLAS-owned domain (later phase)</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="border-b border-border bg-[#FAFAFA]">
        <div className="mx-auto max-w-[1280px] px-6 py-16 lg:px-10">
          <div className="flex flex-wrap items-center justify-between gap-6">
            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-muted-foreground">
                Begin
              </p>
              <h2 className="mt-3 font-serif text-3xl font-light tracking-tight">
                Shape a candidate use case.
              </h2>
            </div>
            <div className="flex flex-wrap items-center gap-4">
              <Button
                asChild
                data-testid="about-cta-explore"
                className="rounded-none px-6 font-mono text-xs uppercase tracking-[0.18em]"
              >
                <Link to="/explore">Start guided flow →</Link>
              </Button>
              <Button
                asChild
                variant="outline"
                data-testid="about-cta-wallet"
                className="rounded-none border-foreground/40 bg-white font-mono text-xs uppercase tracking-[0.18em] hover:border-foreground"
              >
                <Link to="/wallet">Open wallet</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
